import { getCssUrl } from '../../../config/runtime'

export enum WebAwesomeComponent {
  Avatar = 'wa-avatar',
  Badge = 'wa-badge',
  Button = 'wa-button',
  ButtonGroup = 'wa-button-group',
  Callout = 'wa-callout',
  Card = 'wa-card',
  Checkbox = 'wa-checkbox',
  CopyButton = 'wa-copy-button', // ! Note: This is a separate component from the CopyButton in the Docs section. This one is for general use, while the Docs one is specifically for copying markup in the ComponentMarkup component.
  Details = 'wa-details',
  Dialog = 'wa-dialog',
  Divider = 'wa-divider',
  Dropdown = 'wa-dropdown',
  DropdownItem = 'wa-dropdown-item',
  Icon = 'wa-icon',
  Input = 'wa-input',
  Spinner = 'wa-spinner',
  Tooltip = 'wa-tooltip',
  Tree = 'wa-tree',
  TreeItem = 'wa-tree-item',
  Skeleton = 'wa-skeleton',
}

function tryLoadDependency(doc: Document, component: WebAwesomeComponent): void {
  if (doc?.getElementsByTagName(component)?.length === 0) {
    const newElement = document.createElement(component)
    newElement.setAttribute('style', 'display: none')
    newElement.textContent = 'AA autoloaded'
    doc.body.appendChild(newElement)
  }
}

export function tryLoadDependencies(doc: Document, components: WebAwesomeComponent[]): void {
  components.forEach((component) => {
    tryLoadDependency(doc, component)
  })
}

// Load light DOM styles for all components
// ! Remove tryLoadLightDOMStyles method from your web component constructor if Light DOM is not needed
export function tryLoadLightDOMStyles(cssFileName: string): void {
  const lightDOMCssUrl = getComponentCssUrl(`${cssFileName}`)
  const linkId = `lightdom-${cssFileName.replace('.css', '').toLowerCase()}`

  // Check if already loaded to avoid duplicates
  if (!document.getElementById(linkId)) {
    const link = document.createElement('link')
    link.id = linkId
    link.rel = 'stylesheet'
    link.href = lightDOMCssUrl
    document.head.appendChild(link)
  }
}

/**
 * Utility functions for WebComponent runtime configuration
 * Provides centralized access to CDN URLs for global and component-specific assets
 */

/**
 * Get the CDN URL for global CSS files
 * @param filename - Optional specific global CSS file name (defaults to 'trustage-aa--wc.css')
 * @returns Full CDN URL for the global CSS file
 */
export function getGlobalCssUrl(filename: string = 'trustage-aa--wc.css'): string {
  return getCssUrl(filename)
}

/**
 * Get the CDN URL for component-specific CSS files
 * @param filename - The component CSS filename (e.g., 'Property.css')
 * @returns Full CDN URL for the component CSS file
 */
export function getComponentCssUrl(filename: string): string {
  return getCssUrl(`components/${filename}`)
}

/**
 * Registers the Bootstrap icon library with WebAwesome so that
 * wa-icon elements with library="bootstrap" resolve to Bootstrap Icons CDN SVGs.
 *
 * Must be called once at application startup (e.g., in SettingsManager or main.ts).
 * Safe to call multiple times — skips registration if already registered.
 */
export function registerBootstrapIconLibrary(): void {
  const waLoaderUrl = 'https://early.webawesome.com/webawesome@3.0.0-beta.6/dist/webawesome.js'
  import(waLoaderUrl)
    .then((wa) => {
      if (typeof wa.registerIconLibrary === 'function') {
        wa.registerIconLibrary('bootstrap', {
          resolver: (name: string, family: string) => {
            const suffix = family === 'filled' ? '-fill' : ''
            return `https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/icons/${name}${suffix}.svg`
          },
          mutator: (svg: SVGElement) => {
            svg.setAttribute('fill', 'currentColor')
            svg.setAttribute('width', '1em')
            svg.setAttribute('height', '1em')
          },
        })
      }
    })
    .catch(() => {
      // Silently fail — icon library registration is non-critical
    })
}
